/**
 * @file 教师关联学员列表弹窗
 * @description 按教师 id 调 `/pc/student/list`，展平该教师名下学员科目行。
 *
 * @whenToUse 教师管理列表「学员人数」入口。
 * @whenNotToUse 学员管理主列表；校区成员名单。
 */

import { useEffect, useState } from 'react'
import { Badge, type BadgeVariant } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  computeConsecutiveRowSpan,
  DataTable,
  type DataTableColumn,
} from '@/components/ui/data-table.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { TeacherItem } from '@/api/teacher.ts'
import { fetchStudentList } from '@/api/student.ts'
import {
  GRADE_OPTIONS,
  LEARNING_LEVEL_OPTIONS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  flattenStudentRows,
  optionLabel,
  type StudentListRow,
  type StudentStatus,
} from '@/components/pages/student-page/types.ts'

const STATUS_BADGE: Record<StudentStatus, BadgeVariant> = {
  '1': 'success',
  '2': 'warning',
  '0': 'danger',
}

export type TeacherStudentsDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 教师行 */
  teacher: TeacherItem | null
}

/**
 * 教师学员名单 Dialog
 */
export function TeacherStudentsDialog({
  open,
  onOpenChange,
  teacher,
}: TeacherStudentsDialogProps) {
  const [rows, setRows] = useState<StudentListRow[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !teacher) return
    setLoading(true)
    // 单教师名下学员量级有限，一次取回后本地分页
    fetchStudentList({ teacherId: teacher.id, pageSize: 500 })
      .then((result) => {
        setRows(
          flattenStudentRows(result.rows).filter(
            (row) => row.teacherId === teacher.id,
          ),
        )
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, teacher])

  const studentRowSpan: DataTableColumn<StudentListRow>['onCell'] = (
    _record,
    index,
    data,
  ) => ({
    rowSpan: computeConsecutiveRowSpan(data, index, (row) => row.studentId),
  })

  const columns: DataTableColumn<StudentListRow>[] = [
    {
      key: 'studentName',
      title: '学员名称',
      dataIndex: 'studentName',
      minWidth: 100,
      onCell: studentRowSpan,
    },
    {
      key: 'phone',
      title: '手机号',
      dataIndex: 'phone',
      minWidth: 120,
      onCell: studentRowSpan,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 72,
      onCell: studentRowSpan,
    },
    {
      key: 'status',
      title: '状态',
      minWidth: 88,
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={STATUS_BADGE[row.status]}>
          {STUDENT_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'subject',
      title: '科目',
      minWidth: 72,
      className: 'border-l border-border',
      render: (row) => optionLabel(SUBJECT_OPTIONS, row.subject),
    },
    {
      key: 'textbook',
      title: '教材',
      dataIndex: 'textbookName',
      minWidth: 120,
    },
    {
      key: 'grade',
      title: '年级',
      minWidth: 72,
      render: (row) => optionLabel(GRADE_OPTIONS, row.grade),
    },
    {
      key: 'semester',
      title: '学期',
      minWidth: 72,
      render: (row) => optionLabel(SEMESTER_OPTIONS, row.semester),
    },
    {
      key: 'stage',
      title: '阶段',
      minWidth: 72,
      render: (row) => optionLabel(STAGE_OPTIONS, row.educationStage),
    },
    {
      key: 'learningLevel',
      title: '学习力等级',
      minWidth: 96,
      render: (row) => optionLabel(LEARNING_LEVEL_OPTIONS, row.learningGrade),
    },
    {
      key: 'learningPower',
      title: '学习力',
      dataIndex: 'learningForce',
      minWidth: 72,
    },
  ]

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${teacher?.teacherName ?? ''} · 学员列表`}
      className="max-w-6xl"
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          关闭
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        pagination={{ page: 1, pageSize: 10, total: rows.length, onChange: () => {} }}
      />
    </Dialog>
  )
}
